import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PiCaretDownBold, PiCaretRightBold } from "react-icons/pi";
import client from "../../api/client";

function CohortsPanel({ courseId }) {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get(`/admin/courses/${courseId}/cohorts`)
      .then(res => setCohorts(res.data))
      .catch(() => toast.error("Failed to load cohorts."))
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
      <div className="h-8 bg-gray-200 rounded animate-pulse w-48" />
    </div>
  );

  if (cohorts.length === 0) return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-400">No cohorts linked to this course.</div>
  );

  return (
    <div className="bg-gray-50 border-t border-gray-100 px-6 py-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Cohorts</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cohorts.map(c => (
          <button key={c.id}
            onClick={() => navigate(`/dashboard/admin/courses/${courseId}/cohorts/${c.id}`)}
            className="text-left p-3 bg-white rounded-xl border border-gray-200 hover:border-[#294a93]/40 hover:shadow-sm transition-all group">
            <p className="font-semibold text-gray-800 text-sm group-hover:text-[#294a93] transition-colors">{c.name}</p>
            <p className="text-xs text-gray-400 mt-1">
              {c.start_date ? new Date(c.start_date).toLocaleDateString() : "—"}
              {c.end_date ? ` → ${new Date(c.end_date).toLocaleDateString()}` : ""}
            </p>
            <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-semibold ${c.is_open ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {c.is_open ? "Open" : "Closed"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/admin/courses")
      .then(res => setCourses(res.data))
      .catch(() => toast.error("Failed to load courses."))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => prev === id ? null : id);

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Courses</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {courses.length} active courses — click a course to see its cohorts and manage classes
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-white rounded-2xl border border-gray-100 animate-pulse" />)}</div>
      ) : courses.length === 0 ? (
        <div className="p-16 text-center bg-white rounded-2xl border border-gray-100">
          <p className="text-4xl mb-3">📚</p>
          <p className="text-sm font-medium text-gray-600">No active courses</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
          {courses.map(course => (
            <div key={course.id}>
              <button
                onClick={() => toggle(course.id)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left">
                {course.image_url ? (
                  <img src={course.image_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0 bg-gray-100" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#294a93]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#294a93] text-lg font-bold">{course.title[0]}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">{course.title}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400">{course.cohorts_count ?? 0} cohort{(course.cohorts_count ?? 0) !== 1 ? "s" : ""}</span>
                    <span className="text-xs text-gray-400">{course.subscribers_count ?? 0} enrolled</span>
                    {course.type && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#294a93]/10 text-[#294a93] font-semibold capitalize">{course.type}</span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 text-gray-400">
                  {expanded === course.id
                    ? <PiCaretDownBold size={16} />
                    : <PiCaretRightBold size={16} />}
                </div>
              </button>

              {expanded === course.id && <CohortsPanel courseId={course.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
